import api from '../../../services/api';

/**
 * Fetches proposals using the new scoped API for Team Leads or Admins.
 * Handles search, filter by stage_code, and pagination (skip/limit).
 */
export const fetchProposals = async (params, isAdmin = false) => {
    // Dynamically select endpoint based on user role (Lead vs Admin)
    const endpoint = isAdmin ? '/rfp/admin/scoped/v1/rfps' : '/rfp/lead/scoped/v1/rfps';
    const response = await api.get(endpoint, { params });
    return response.data;
};

/**
 * Legacy Employee fetch - moved to a specific function if needed, 
 * but primary fetchProposals now uses the scoped endpoint.
 */
export const fetchEmployeeProposals = async (params) => {
    try {
        const response = await api.get('/rfp/employee/my-assignments', { params });
        const groups = response.data?.groups || [];
        return groups.map(g => ({
            ...g,
            id: g.rfp_id,
            rfp_id: g.rfp_id,
            completed_sections: g.completed_sections ?? 0,
            total_sections: g.total_sections ?? (g.sections?.length ?? 0),
            created_at: g.latest_assigned_at,
            sections: g.sections || [],
            isAssignment: true
        }));
    } catch (error) {
        console.error("Employee assignments fetch failed:", error);
        return [];
    }
};

export const fetchProposalMetrics = async (isAdmin = false) => {
    const endpoint = isAdmin ? '/rfp/admin/scoped/v1/summary' : '/rfp/lead/scoped/v1/summary';
    const response = await api.get(endpoint);
    return response.data;
};

export const deleteProposal = async (id) => {
    return await api.delete(`/rfp/${id}`);
};

export const deleteProposalsBulk = async (ids) => {
    return await api.delete(`/rfp/bulk`, { data: { rfp_ids: ids } });
};

/**
 * Bulk Download RFP Reports as ZIP
 * POST /rfp/report/bulk-download
 *
 * Generates reports for multiple RFPs in a single request and receives them
 * packaged inside a ZIP archive. Each file inside the ZIP is named after the
 * proposal title (human-readable) rather than the raw RFP UUID.
 *
 * @param {Object|string[]|string} options - Payload object, array of IDs, or comma-separated IDs
 * @param {string[]} [options.rfp_ids] - Array of RFP IDs
 * @param {string} [options.format='pdf'] - Report format ('pdf', etc.)
 * @param {string} [options.language='en'] - Language code ('en', etc.)
 * @param {string} [options.template_id] - Optional template ID
 * @returns {Promise<AxiosResponse<Blob>>}
 */
export const bulkDownloadReports = async (options = {}) => {
    let payload;

    if (Array.isArray(options)) {
        payload = { rfp_ids: options.map(String), format: 'pdf', language: 'en', theme: 'default', template_id: 'default' };
    } else if (typeof options === 'string') {
        payload = { rfp_ids: options.split(',').map(s => s.trim()).filter(Boolean), format: 'pdf', language: 'en', theme: 'default', template_id: 'default' };
    } else {
        const rawIds = options.rfp_ids || options.ids || [];
        payload = {
            rfp_ids: Array.isArray(rawIds) ? rawIds.map(String) : [String(rawIds)],
            format: options.format || 'pdf',
            language: options.language || 'en',
            theme: options.theme || 'default',
            template_id: options.template_id || 'default'
        };
    }

    return await api.post('/rfp/report/bulk-download', payload, {
        responseType: 'blob'
    });
};

export const downloadProposalsBulk = bulkDownloadReports;

export const fetchFullProposalData = async (id) => {
    return await api.get(`/rfp/${id}`);
};

// Keeping this for backward compatibility during transition
export const fetchScopedProposals = fetchProposals;
